<template>
  <div class="parking-area-manage">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('parkingAreaManage.page.title')" :subtitle="$t('parkingAreaManage.page.subtitle')">
      <template #extra>
        <el-button v-if="hasPrivilege('502022100147030003')" type="primary" icon="el-icon-plus"
          @click="_openAddParkingAreaModal">
          {{ $t('common.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="parkingAreaManageInfo.conditions.num" clearable class="w-full"
            :placeholder="$t('parkingAreaManage.inputParkingNum')" @keyup.enter.native="_queryParkingAreaMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="parkingAreaManageInfo.conditions.typeCd" clearable class="w-full"
            :placeholder="$t('parkingAreaManage.selectParkingType')">
            <el-option v-for="item in parkingTypes" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="_queryParkingAreaMethod">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="_resetParkingAreaMethod">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false"
        :show-refresh="true" @update:density="density = $event" @refresh="_listParkingAreas">
        <template #left>
          <span class="ds-panel__title">{{ $t('parkingAreaManage.parkingInfo') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="parkingAreaManageInfo.parkingAreas" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border :empty-description="$t('parkingAreaManage.page.empty')"
        @retry="_listParkingAreas">
        <el-table-column prop="paId" :label="$t('parkingAreaManage.parkingId')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.paId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="num" :label="$t('parkingAreaManage.parkingNum')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.num || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('parkingAreaManage.parkingType')" align="center" width="130">
          <template slot-scope="scope">
            <ds-status-tag size="sm" :type="scope.row.typeCd === '1001' ? 'primary' : 'info'"
              :label="scope.row.typeCd === '1001' ? $t('parkingAreaManage.aboveGround') : $t('parkingAreaManage.underground')" />
          </template>
        </el-table-column>
        <el-table-column v-for="(item, index) in parkingAreaManageInfo.listColumns" :key="index"
          :label="item.specName" align="center" min-width="120">
          <template slot-scope="scope">
            {{ scope.row.listValues[index] }}
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('parkingAreaManage.remark')" align="center" min-width="120"
          show-overflow-tooltip />
        <el-table-column prop="createTime" :label="$t('parkingAreaManage.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="250" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="hasPrivilege('502022100103010004')" size="mini"
              @click="_openEditParkingAreaModel(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button v-if="hasPrivilege('502022100117490005')" size="mini" type="danger"
              @click="_openDeleteParkingAreaModel(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :page="pagination.current" :size="pagination.size" :total="pagination.total"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <add-parking-area ref="addModal" @success="handleAddSuccess" />
    <edit-parking-area ref="editModal" @success="handleEditSuccess" />
    <delete-parking-area ref="deleteModal" @success="handleDeleteSuccess" />
  </div>
</template>

<script>
import { getParkingAreaList } from '@/api/car/parkingAreaManageApi'
import AddParkingArea from '@/components/car/addParkingArea'
import EditParkingArea from '@/components/car/editParkingArea'
import DeleteParkingArea from '@/components/car/deleteParkingArea'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ParkingAreaManageList',
  components: {
    AddParkingArea,
    EditParkingArea,
    DeleteParkingArea,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      parkingAreaManageInfo: {
        parkingAreas: [],
        listColumns: [],
        conditions: {
          num: '',
          typeCd: '',
          paId: ''
        }
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      parkingTypes: [
        { value: '1001', label: this.$t('parkingAreaManage.aboveGround') },
        { value: '2001', label: this.$t('parkingAreaManage.underground') }
      ]
    }
  },
  created() {
    this._getColumns()
    this._listParkingAreas()
  },
  methods: {
    async _listParkingAreas() {
      this.loading = true
      this.hasError = false
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          ...this.parkingAreaManageInfo.conditions,
          communityId: getCommunityId()
        }

        const res = await getParkingAreaList(params)
        this.parkingAreaManageInfo.parkingAreas = res.parkingAreas
        this.pagination.total = res.total
        this.dealParkingAreaAttr(res.parkingAreas)
      } catch (error) {
        console.error('获取停车场列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('parkingAreaManage.fetchError')
        this.$message.error(this.$t('parkingAreaManage.fetchError'))
      } finally {
        this.loading = false
      }
    },

    _queryParkingAreaMethod() {
      this.pagination.current = 1
      this._listParkingAreas()
    },

    _resetParkingAreaMethod() {
      this.parkingAreaManageInfo.conditions = {
        num: '',
        typeCd: '',
        paId: ''
      }
      this.pagination.current = 1
      this._listParkingAreas()
    },

    _openAddParkingAreaModal() {
      this.$refs.addModal.open()
    },

    _openEditParkingAreaModel(row) {
      this.$refs.editModal.open(row)
    },

    _openDeleteParkingAreaModel(row) {
      this.$refs.deleteModal.open(row)
    },

    handleAddSuccess() {
      this._listParkingAreas()
    },

    handleEditSuccess() {
      this._listParkingAreas()
    },

    handleDeleteSuccess() {
      this._listParkingAreas()
    },

    handleSizeChange(size) {
      this.pagination.size = size
      this._listParkingAreas()
    },

    handleCurrentChange(current) {
      this.pagination.current = current
      this._listParkingAreas()
    },

    dealParkingAreaAttr(parkingAreas) {
      parkingAreas.forEach(item => {
        this._getColumnsValue(item)
      })
    },

    _getColumnsValue(parkingArea) {
      parkingArea.listValues = []
      if (!parkingArea.attrs || parkingArea.attrs.length === 0) {
        this.parkingAreaManageInfo.listColumns.forEach(() => {
          parkingArea.listValues.push('')
        })
        return
      }

      this.parkingAreaManageInfo.listColumns.forEach(column => {
        const attr = parkingArea.attrs.find(a => a.specCd === column.specCd)
        parkingArea.listValues.push(attr ? attr.value : '')
      })
    },

    async _getColumns() {
      try {
        // 这里需要根据实际接口获取列配置
        // 示例代码，实际需要替换为API调用
        this.parkingAreaManageInfo.listColumns = [
        ]
      } catch (error) {
        console.error('获取列配置失败:', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.parking-area-manage {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>
